import { useState } from "react";
import { Mail, Phone, Clock, Check } from "lucide-react";
import { submitConsultation } from "@/lib/api";
import { BRAND, IMAGES } from "@/lib/constants";

const INVESTMENT_TYPES = [
  "Land Development",
  "Property Flipping",
  "International Investment",
  "Project Management",
  "General Inquiry",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    investment_type: INVESTMENT_TYPES[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitConsultation(form);
      setSuccess(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#0A1628]">
      {/* HEADER */}
      <section data-testid="contact-header" className="pt-40 pb-12 md:pb-16 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-2">
            <div className="overline">Contact</div>
          </div>
          <div className="lg:col-span-10">
            <h1 className="font-serif-display text-6xl md:text-8xl lg:text-[9rem] text-[#F3EFE4] leading-[0.88]">
              Let's start the <span className="italic text-[#D4AF37]">conversation.</span>
            </h1>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <section className="py-12 md:py-20 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* LEFT — info */}
          <div className="lg:col-span-5">
            <div className="overline mb-6">Reach us directly</div>
            <p className="text-[#8FA3B8] leading-relaxed mb-10 text-base md:text-lg">
              Whether you're an investor exploring Edmonton or the Caribbean, or a local owner with a property to develop — we'd be glad to hear from you.
            </p>

            {/* Two offices */}
            <div className="space-y-6 mb-10">
              <div data-testid="office-canada" className="border border-white/10 p-6 bg-[#0F1F35]">
                <div className="overline !text-[0.62rem] mb-2 flex items-center gap-2">
                  🇨🇦 <span>Canada · Headquarters</span>
                </div>
                <h3 className="font-serif-display text-2xl text-[#F3EFE4] mb-1">Edmonton, Alberta</h3>
                <div className="text-sm text-[#8FA3B8]">Executive leadership, capital, Alberta development</div>
              </div>
              <div data-testid="office-stlucia" className="border border-[#D4AF37]/40 p-6 bg-gradient-to-br from-[#0F1F35] to-[#0A1628]">
                <div className="overline !text-[0.62rem] mb-2 flex items-center gap-2">
                  🇱🇨 <span>St. Lucia · Caribbean Ops</span>
                </div>
                <h3 className="font-serif-display text-2xl text-[#F3EFE4] mb-1">Canelles, St. Lucia</h3>
                <div className="text-sm text-[#8FA3B8]">Site management & Caribbean investor liaison</div>
              </div>
            </div>

            <div className="space-y-6">
              <ContactRow icon={<Mail size={18} />} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} testId="contact-email" />
              <ContactRow icon={<Phone size={18} />} label="Phone (Canada)" value={BRAND.phone} href={`tel:${BRAND.phone.replace(/\D/g, "")}`} testId="contact-phone" />
              <ContactRow icon={<Clock size={18} />} label="Hours" value="Mon – Fri · 9:00 AM – 6:00 PM MT" testId="contact-hours" />
            </div>
          </div>

          {/* RIGHT — form */}
          <div data-testid="contact-form-wrap" className="lg:col-span-7 bg-[#12253D] border border-white/10 p-8 md:p-12">
            {!success ? (
              <form onSubmit={handleSubmit}>
                <div className="overline mb-4">Consultation Request</div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-[#F3EFE4] mb-2">
                  Tell us about your project.
                </h2>
                <p className="text-[#8FA3B8] text-sm mb-10">
                  We'll respond within 24 business hours.
                </p>

                <div className="grid md:grid-cols-2 gap-x-8 gap-y-7">
                  <div>
                    <label className="field-label" htmlFor="c-name">Full Name *</label>
                    <input id="c-name" data-testid="contact-input-name" className="field-input" value={form.name} onChange={update("name")} required />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="c-email">Email *</label>
                    <input id="c-email" type="email" data-testid="contact-input-email" className="field-input" value={form.email} onChange={update("email")} required />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="c-phone">Phone</label>
                    <input id="c-phone" data-testid="contact-input-phone" className="field-input" value={form.phone} onChange={update("phone")} />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="c-type">Interest</label>
                    <select id="c-type" data-testid="contact-input-type" className="field-input" value={form.investment_type} onChange={update("investment_type")}>
                      {INVESTMENT_TYPES.map((t) => (
                        <option key={t} value={t} className="bg-[#12253D]">{t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label" htmlFor="c-message">Message *</label>
                    <textarea id="c-message" rows={5} data-testid="contact-input-message" className="field-input resize-none" value={form.message} onChange={update("message")} required />
                  </div>
                </div>

                {error && (
                  <div data-testid="contact-error" className="mt-6 text-sm text-red-400 border border-red-500/30 px-4 py-3">
                    {error}
                  </div>
                )}

                <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                  <p className="text-xs text-[#8FA3B8] max-w-xs">By submitting, you agree we may contact you about your inquiry.</p>
                  <button type="submit" data-testid="contact-submit" disabled={submitting} className="btn-primary disabled:opacity-50">
                    {submitting ? "Sending..." : "Send Request"}
                    {!submitting && <span className="btn-arrow">→</span>}
                  </button>
                </div>
              </form>
            ) : (
              <div data-testid="contact-success" className="text-center py-12">
                <div className="mx-auto mb-8 w-16 h-16 border border-[#D4AF37] flex items-center justify-center">
                  <Check className="text-[#D4AF37]" size={28} />
                </div>
                <div className="overline mb-3">Request received</div>
                <h3 className="font-serif-display text-3xl md:text-4xl text-[#F3EFE4] mb-4">Thank you, {form.name.split(" ")[0]}.</h3>
                <p className="text-[#8FA3B8] max-w-md mx-auto">
                  Your message has been logged. A member of our team will be in touch within 24 hours at <span className="text-[#F3EFE4]">{form.email}</span>.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* FAQ-ish callout */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-3 gap-8">
          <Callout title="Local investors" desc="Edmonton-based clients can request an in-person site walk or office consultation." />
          <Callout title="International investors" desc="We coordinate calls across time zones and handle local representation, end-to-end." />
          <Callout title="Industry partners" desc="Brokers, trades, and capital partners — we're always open to building the right relationships." />
        </div>
      </section>
    </div>
  );
}

function ContactRow({ icon, label, value, href, testId }) {
  const content = (
    <div className="flex items-start gap-5 group">
      <div className="w-10 h-10 border border-white/15 flex items-center justify-center text-[#D4AF37] group-hover:border-[#D4AF37] transition shrink-0">
        {icon}
      </div>
      <div>
        <div className="overline !text-[0.62rem] mb-1 !text-[#8FA3B8]">{label}</div>
        <div className="text-[#F3EFE4] text-base md:text-lg">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testId} className="block hover:text-[#D4AF37] transition">{content}</a>
  ) : (
    <div data-testid={testId}>{content}</div>
  );
}

function Callout({ title, desc }) {
  return (
    <div className="border border-white/10 p-8 hover:border-[#D4AF37]/40 transition">
      <h3 className="font-serif-display text-2xl text-[#F3EFE4] mb-3">{title}</h3>
      <p className="text-sm text-[#8FA3B8] leading-relaxed">{desc}</p>
    </div>
  );
}
